'use client';

import { c as _c } from "react/compiler-runtime";
import { type CancelProposalJobResponse, type CreateProposalJobResponse, CUSTOM_GATEWAY_ORIGIN, type GetProposalJobRow, type StreamFrame, type SubscribeProposalJobEventsEventName, useCancelProposalJob, useCreateProposalJob, useGetProposalJob, useSubscribeProposalJobEvents } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { type MutableRefObject, useEffect, useRef } from 'react';
import { preconnect } from 'react-dom';
import { TOAST_IDS } from '@/config/toast-ids';
import { applyProposalJobSnapshot, applyProposalProgressEvent, type ProposalJobSnapshot, type ProposalProgressEventData, type SequencedProposalProgressEvent } from '@/lib/ai/proposal-progress-controller';
import { minutesUntilReset, showRateLimitedToast } from '@/lib/sse/rate-limit-toast';
import type { Proposal } from '@/lib/sse/sse-events';
import { logger } from '@/lib/telemetry/logger';
import { showToast } from '@/lib/ui/toast';
import { type AiErrorCode, type AiStreamStatus, useProposalFormStore } from '@/stores/proposal-form';
interface UseProposalGenerationApi {
  readonly status: AiStreamStatus;
  readonly start: (prompt: string) => Promise<void>;
  readonly reconnect: () => Promise<void>;
  readonly cancel: () => void;
  readonly cancelSubscriptionOnly: () => void;
}
const PROPOSAL_JOB_SNAPSHOT_FIELDS = ['jobId', 'status', 'lastSeq', 'text', 'images', 'proposal', 'classification', 'error'] as const;
function isRecord(value: ProposalProgressEventData): value is Record<string, ProposalProgressEventData> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function progressEventFromFrame(frame: StreamFrame<SubscribeProposalJobEventsEventName>): SequencedProposalProgressEvent {
  const data = frame.data as ProposalProgressEventData;
  if (isRecord(data) && typeof data.event === 'string') {
    return {
      event: data.event,
      data: 'data' in data ? data.data : {},
      ...(typeof data.seq === 'number' ? {
        seq: data.seq
      } : {}),
      ...(typeof data.emittedAt === 'string' ? {
        emittedAt: data.emittedAt
      } : {})
    };
  }
  return {
    event: frame.event,
    data
  };
}
type ProposalJobSnapshotDto = CreateProposalJobResponse['snapshot'] | CancelProposalJobResponse | GetProposalJobRow;
function normalizeProposal(proposal: ProposalJobSnapshotDto['proposal']): Proposal | undefined {
  if (proposal === undefined) return undefined;
  return {
    title: proposal.title,
    marketTicker: proposal.marketTicker,
    category: proposal.category,
    location: proposal.location,
    description: proposal.description,
    outcomes: proposal.outcomes.map(outcome => ({
      label: outcome.label,
      ticker: outcome.ticker,
      imageUrl: outcome.imageUrl,
      imageQuery: outcome.imageQuery
    })),
    coverImageUrl: proposal.coverImageUrl,
    coverImageQuery: proposal.coverImageQuery,
    ...(proposal.classification !== undefined ? {
      classification: proposal.classification
    } : undefined)
  };
}
function errorCodeFromSnapshot(error: ProposalJobSnapshotDto['error']): string | undefined {
  if (error === undefined || !('code' in error)) return undefined;
  return typeof error.code === 'string' ? error.code : undefined;
}
function normalizeProposalJobSnapshot(snapshot: ProposalJobSnapshotDto): ProposalJobSnapshot {
  const userPubkey = 'userPubkey' in snapshot ? snapshot.userPubkey : '';
  const createdAt = 'createdAt' in snapshot ? snapshot.createdAt : '';
  const updatedAt = 'updatedAt' in snapshot ? snapshot.updatedAt : '';
  const proposal = normalizeProposal(snapshot.proposal);
  const errorCode = errorCodeFromSnapshot(snapshot.error);
  return {
    jobId: snapshot.jobId,
    userPubkey,
    status: snapshot.status,
    lastSeq: snapshot.lastSeq,
    createdAt,
    updatedAt,
    text: {
      ...(snapshot.text.title !== undefined ? {
        title: snapshot.text.title
      } : {}),
      ...(snapshot.text.marketTicker !== undefined ? {
        marketTicker: snapshot.text.marketTicker
      } : {}),
      ...(snapshot.text.category !== undefined ? {
        category: snapshot.text.category
      } : {}),
      ...(snapshot.text.location !== undefined ? {
        location: snapshot.text.location
      } : {}),
      ...(snapshot.text.description !== undefined ? {
        description: snapshot.text.description
      } : {}),
      ...(snapshot.text.outcomes !== undefined ? {
        outcomes: snapshot.text.outcomes.map(outcome => ({
          label: outcome.label,
          ticker: outcome.ticker
        }))
      } : {})
    },
    images: {
      expected: snapshot.images.expected,
      previewsSettled: snapshot.images.previewsSettled,
      finalized: snapshot.images.finalized,
      failed: snapshot.images.failed
    },
    ...(proposal !== undefined ? {
      proposal
    } : {}),
    ...(snapshot.classification !== undefined ? {
      classification: snapshot.classification
    } : undefined),
    ...(snapshot.error !== undefined ? {
      error: {
        stage: snapshot.error.stage,
        message: snapshot.error.message,
        retryable: snapshot.error.retryable,
        ...(errorCode !== undefined ? {
          code: errorCode
        } : {})
      }
    } : {})
  };
}
function snapshotFromCreateResponse(response: CreateProposalJobResponse): ProposalJobSnapshot {
  return normalizeProposalJobSnapshot(response.snapshot);
}
function isTerminalSnapshot(snapshot: ProposalJobSnapshot): boolean {
  return snapshot.status === 'create_ready' || snapshot.status === 'cancelled' || snapshot.status === 'failed' || snapshot.status === 'finalization_failed';
}
function requireProposalJobSnapshot(snapshot: GetProposalJobRow | null | undefined): GetProposalJobRow {
  if (snapshot === null || snapshot === undefined) {
    throw new Error('proposal job snapshot not found');
  }
  return snapshot;
}
function preconnectProposalImageGateway(preconnectedRef: MutableRefObject<boolean>): void {
  if (preconnectedRef.current) return;
  preconnectedRef.current = true;
  preconnect(CUSTOM_GATEWAY_ORIGIN);
}
function normalizeAiErrorCode(code: string | undefined): AiErrorCode | undefined {
  if (code === 'network' || code === 'rate_limited' || code === 'ai_market_content_policy') {
    return code;
  }
  return undefined;
}
export function useProposalGeneration() {
  const $ = _c(45);
  const t = useTranslations("aiProposal.errors");
  const tForm = useTranslations("launchpad.form");
  const createJob = useCreateProposalJob();
  const cancelJob = useCancelProposalJob();
  const eventStream = useSubscribeProposalJobEvents();
  const pendingCancelRef = useRef(false);
  const imageGatewayPreconnectedRef = useRef(false);
  const currentJobId = useProposalFormStore(_temp);
  const t0 = currentJobId ?? "";
  let t1;
  if ($[0] !== t0) {
    t1 = {
      jobId: t0,
      fields: PROPOSAL_JOB_SNAPSHOT_FIELDS
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      enabled: false
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const getJob = useGetProposalJob(t1, t2);
  const setAiStreamStatus = useProposalFormStore(_temp2);
  const setAiError = useProposalFormStore(_temp3);
  const resetForAiGeneration = useProposalFormStore(_temp4);
  const clearInFlightImageUploads = useProposalFormStore(_temp5);
  const setAiCancelRequested = useProposalFormStore(_temp6);
  const setAiCreateReadyStatus = useProposalFormStore(_temp7);
  let t3;
  let t4;
  if ($[3] !== clearInFlightImageUploads || $[4] !== eventStream.status || $[5] !== setAiStreamStatus) {
    t3 = () => {
      setAiStreamStatus(eventStream.status as AiStreamStatus);
      if (eventStream.status === "done" || eventStream.status === "error") {
        clearInFlightImageUploads();
      }
    };
    t4 = [eventStream.status, setAiStreamStatus, clearInFlightImageUploads];
    $[3] = clearInFlightImageUploads;
    $[4] = eventStream.status;
    $[5] = setAiStreamStatus;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  const lastErrorRef = useRef(null);
  let t5;
  let t6;
  if ($[8] !== eventStream.error || $[9] !== setAiError || $[10] !== t) {
    t5 = () => {
      if (eventStream.error === null) {
        lastErrorRef.current = null;
        return;
      }
      const key = `${eventStream.error.stage}|${eventStream.error.message}`;
      if (lastErrorRef.current === key) {
        return;
      }
      lastErrorRef.current = key;
      if (eventStream.error.code === "rate_limited" && eventStream.error.resetMs !== undefined) {
        const minutes = minutesUntilReset(eventStream.error.resetMs);
        showRateLimitedToast({
          message: t("rateLimited", {
            minutes
          })
        });
        setAiError({
          stage: "rate_limited",
          message: t("rateLimited", {
            minutes
          }),
          retryable: true,
          code: "rate_limited",
          resetMs: eventStream.error.resetMs,
          ...(eventStream.error.retryAfterSeconds !== undefined ? {
            retryAfterSeconds: eventStream.error.retryAfterSeconds
          } : {})
        });
        return;
      }
      const code = normalizeAiErrorCode(eventStream.error.code);
      setAiError({
        stage: eventStream.error.stage,
        message: eventStream.error.message,
        retryable: eventStream.error.retryable,
        ...(code !== undefined ? {
          code
        } : {})
      });
    };
    t6 = [eventStream.error, setAiError, t];
    $[8] = eventStream.error;
    $[9] = setAiError;
    $[10] = t;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  useEffect(t5, t6);
  let t7;
  if ($[13] !== tForm) {
    t7 = frame => {
      applyProposalProgressEvent(useProposalFormStore.getState(), progressEventFromFrame(frame), {
        onCoverPinFailed: () => {
          showToast({
            id: `${TOAST_IDS.IMAGE_LOAD_FAILED}_cover`,
            message: tForm("coverImageFailedTitle"),
            description: tForm("coverImageFailedDescription"),
            variant: "error",
            duration: 10000,
            action: {
              label: tForm("coverImageFailedAction"),
              onClick: _temp8
            }
          });
        },
        onOutcomePinFailed: failedIndex => {
          showToast({
            id: `${TOAST_IDS.IMAGE_LOAD_FAILED}_outcome-${String(failedIndex)}`,
            message: tForm("outcomeImageFailedTitle", {
              number: failedIndex + 1
            }),
            description: tForm("outcomeImageFailedDescription"),
            variant: "error",
            duration: 10000,
            action: {
              label: tForm("outcomeImageFailedAction"),
              onClick: () => useProposalFormStore.getState().setOutcomeImage(failedIndex, "", false)
            }
          });
        }
      });
    };
    $[13] = tForm;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const handleEvent = t7;
  let t8;
  if ($[15] !== cancelJob || $[16] !== createJob || $[17] !== eventStream || $[18] !== handleEvent || $[19] !== resetForAiGeneration || $[20] !== setAiCreateReadyStatus || $[21] !== setAiError || $[22] !== setAiStreamStatus || $[23] !== t) {
    t8 = async prompt => {
      preconnectProposalImageGateway(imageGatewayPreconnectedRef);
      pendingCancelRef.current = false;
      resetForAiGeneration();
      setAiStreamStatus("streaming");
      ;
      try {
        const created = await createJob.mutateAsync({
          prompt
        });
        applyProposalJobSnapshot(useProposalFormStore.getState(), snapshotFromCreateResponse(created));
        if (pendingCancelRef.current) {
          const snapshot = await cancelJob.mutateAsync({
            jobId: created.jobId
          });
          applyProposalJobSnapshot(useProposalFormStore.getState(), normalizeProposalJobSnapshot(snapshot));
          setAiStreamStatus("done");
          return;
        }
        await eventStream.start({
          jobId: created.jobId,
          afterSeq: created.snapshot.lastSeq
        }, {
          onEvent: handleEvent
        });
      } catch (t9) {
        const error = t9;
        const message = error instanceof Error ? error.message : t("networkError");
        logger.warn("ai_proposal_job_start_failed", {
          error: message
        });
        setAiStreamStatus("error");
        setAiCreateReadyStatus("failed");
        setAiError({
          stage: "start",
          message,
          retryable: true
        });
      }
    };
    $[15] = cancelJob;
    $[16] = createJob;
    $[17] = eventStream;
    $[18] = handleEvent;
    $[19] = resetForAiGeneration;
    $[20] = setAiCreateReadyStatus;
    $[21] = setAiError;
    $[22] = setAiStreamStatus;
    $[23] = t;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const start = t8;
  let t9;
  if ($[25] !== eventStream || $[26] !== getJob || $[27] !== handleEvent || $[28] !== setAiError || $[29] !== setAiStreamStatus || $[30] !== t) {
    t9 = async () => {
      const jobId = useProposalFormStore.getState().aiJobId;
      if (jobId === null) {
        return;
      }
      setAiStreamStatus("streaming");
      ;
      try {
        eventStream.cancel();
        const result = await getJob.refetch();
        const snapshot_0 = requireProposalJobSnapshot(result.data);
        const normalized = normalizeProposalJobSnapshot(snapshot_0);
        applyProposalJobSnapshot(useProposalFormStore.getState(), normalized);
        if (isTerminalSnapshot(normalized)) {
          setAiStreamStatus("done");
          return;
        }
        await eventStream.start({
          jobId,
          afterSeq: normalized.lastSeq
        }, {
          onEvent: handleEvent
        });
      } catch (t10) {
        const error_0 = t10;
        const message_0 = error_0 instanceof Error ? error_0.message : t("networkError");
        logger.warn("ai_proposal_job_reconnect_failed", {
          error: message_0
        });
        setAiStreamStatus("error");
        setAiError({
          stage: "reconnect",
          message: message_0,
          retryable: true
        });
      }
    };
    $[25] = eventStream;
    $[26] = getJob;
    $[27] = handleEvent;
    $[28] = setAiError;
    $[29] = setAiStreamStatus;
    $[30] = t;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  const reconnect = t9;
  let t10;
  if ($[32] !== cancelJob || $[33] !== clearInFlightImageUploads || $[34] !== eventStream || $[35] !== setAiCancelRequested) {
    t10 = () => {
      pendingCancelRef.current = true;
      const jobId_0 = useProposalFormStore.getState().aiJobId;
      setAiCancelRequested(true);
      if (jobId_0 !== null) {
        cancelJob.mutateAsync({
          jobId: jobId_0
        }).then(_temp9);
      }
      eventStream.cancel();
      clearInFlightImageUploads();
    };
    $[32] = cancelJob;
    $[33] = clearInFlightImageUploads;
    $[34] = eventStream;
    $[35] = setAiCancelRequested;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  const cancel = t10;
  let t11;
  if ($[37] !== eventStream) {
    t11 = () => {
      eventStream.cancel();
    };
    $[37] = eventStream;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  const cancelSubscriptionOnly = t11;
  const t12 = eventStream.status as AiStreamStatus;
  let t13;
  if ($[39] !== cancel || $[40] !== cancelSubscriptionOnly || $[41] !== reconnect || $[42] !== start || $[43] !== t12) {
    t13 = {
      status: t12,
      start,
      reconnect,
      cancel,
      cancelSubscriptionOnly
    };
    $[39] = cancel;
    $[40] = cancelSubscriptionOnly;
    $[41] = reconnect;
    $[42] = start;
    $[43] = t12;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  return t13;
}
function _temp9(snapshot_1) {
  applyProposalJobSnapshot(useProposalFormStore.getState(), normalizeProposalJobSnapshot(snapshot_1));
}
function _temp8() {
  return useProposalFormStore.getState().setCoverImage("", false);
}
function _temp7(s_5) {
  return s_5.setAiCreateReadyStatus;
}
function _temp6(s_4) {
  return s_4.setAiCancelRequested;
}
function _temp5(s_3) {
  return s_3.clearInFlightImageUploads;
}
function _temp4(s_2) {
  return s_2.resetForAiGeneration;
}
function _temp3(s_1) {
  return s_1.setAiError;
}
function _temp2(s_0) {
  return s_0.setAiStreamStatus;
}
function _temp(s) {
  return s.aiJobId;
}